/**
 * Positioner logic and Component
 *
 * @author    Frontend Design Systems <FrontendDesignSystems@wayfair.com>
 * @copyright 2018 Wayfair LLC - All rights reserved
 */
import {Component} from 'react';
import PropTypes from 'prop-types';

export const CARET_SIZE = 10;
export const CARET_POSITION = '50%';

export const ALIGNMENT = {
  middle: 'middle',
  beginning: 'beginning',
  end: 'end',
};

export const PLACEMENT = {
  top: 'top',
  bottom: 'bottom',
  left: 'left',
  right: 'right',
};

const getScrollTop = (doc) => {
  // Retrieve value from scrollingElement if available
  if (typeof doc.scrollingElement !== 'undefined') {
    return doc.scrollingElement.scrollTop;
  }
  // fallback to documentElement or body on older browsers
  if (typeof doc.body !== 'undefined') {
    // Both can be defined, but we want to take the one that's not returning 0
    return Math.max(doc.documentElement.scrollTop, doc.body.scrollTop);
  }
  return doc.documentElement.scrollTop;
};

const getScrollLeft = (doc) => {
  // Retrieve value from scrollingElement if available
  if (typeof doc.scrollingElement !== 'undefined') {
    return doc.scrollingElement.scrollLeft;
  }
  // fallback to documentElement or body on older browsers
  if (typeof doc.body !== 'undefined') {
    // Both can be defined, but we want to take the one that's not returning 0
    return Math.max(doc.documentElement.scrollLeft, doc.body.scrollLeft);
  }
  return doc.documentElement.scrollLeft;
};

export const calculatePosition = (doc, win) => (state, props) => {
  // Bail early if the target or container refs aren't defined
  if (!state.target || !state.container) {
    return null;
  }

  const targetBounding = props.getBounding(state.target);
  const containerBounding = props.getBounding(state.container);

  const containerHeight = containerBounding.maxHeight
    ? containerBounding.maxHeight
    : containerBounding.height;
  const containerWidth = containerBounding.maxWidth
    ? containerBounding.maxWidth
    : containerBounding.width;

  const verticalScrollOffset = getScrollTop(doc);
  const horizontalScrollOffset = getScrollLeft(doc);

  const verticalTargetOffset = verticalScrollOffset + targetBounding.top;
  const horizontalTargetOffset = horizontalScrollOffset + targetBounding.left;

  const targetBoundingMidPoint =
    horizontalTargetOffset + targetBounding.width / 2;

  // get the smaller value. clientWidth does not include scrollbar, innerWidth includes it
  const windowWidth = Math.min(doc.documentElement.clientWidth, win.innerWidth);

  const windowHeight = Math.min(
    doc.documentElement.clientHeight,
    win.innerHeight
  );

  const {offset, caretPosition} = props;
  const position = {};
  const calculatedCaretPosition = {};
  let placement = props.placement;
  let alignment = props.alignment;

  switch (props.placement) {
    case 'top': {
      let overflowsLeft = false;
      let overflowsRight = false;
      if (alignment === ALIGNMENT.middle) {
        overflowsRight =
          targetBoundingMidPoint + containerBounding.width / 2 >
          windowWidth + horizontalScrollOffset;
        overflowsLeft =
          horizontalTargetOffset - horizontalScrollOffset <
          containerBounding.width / 2;
      } else if (alignment === ALIGNMENT.end) {
        overflowsRight =
          targetBounding.right > windowWidth + horizontalScrollOffset;
        overflowsLeft =
          targetBounding.right - containerBounding.width <
          horizontalScrollOffset;
      } else if (alignment === ALIGNMENT.beginning) {
        overflowsLeft = targetBounding.left < horizontalScrollOffset;
        overflowsRight =
          targetBounding.left + containerBounding.width >
          windowWidth + horizontalScrollOffset;
      }

      if (overflowsRight && alignment === ALIGNMENT.beginning) {
        // if we are aligned to the beginning and we overflow to the right
        // then flip the alignment
        alignment = ALIGNMENT.end;
      } else if (overflowsLeft && alignment === ALIGNMENT.end) {
        // If we overflow to the left and the alignment is at the end
        // then flip the alignment
        alignment = ALIGNMENT.beginning;
      }

      if (props.alignment === ALIGNMENT.middle) {
        if (overflowsRight) {
          position.left = windowWidth + horizontalScrollOffset - containerWidth;
          // Hardcoded `CARET_SIZE` here is because the width of the caret is important!
          // we need to offset the position of the caret by the width of itself
          calculatedCaretPosition.left =
            caretPosition ||
            targetBoundingMidPoint - position.left - CARET_SIZE;
        } else if (overflowsLeft) {
          position.left = horizontalScrollOffset;
          calculatedCaretPosition.left =
            caretPosition ||
            targetBoundingMidPoint - position.left - CARET_SIZE;
        } else {
          position.left =
            horizontalTargetOffset -
            (containerBounding.width / 2 - targetBounding.width / 2);
          calculatedCaretPosition.left = caretPosition || CARET_POSITION;
        }
      } else if (props.alignment === ALIGNMENT.beginning) {
        position.left = horizontalTargetOffset;
        calculatedCaretPosition.left = caretPosition || CARET_POSITION;
      } else if (props.alignment === ALIGNMENT.end) {
        calculatedCaretPosition.left = caretPosition || CARET_POSITION;
        position.right = targetBounding.right;
      }

      const top = verticalTargetOffset - (containerHeight + offset);
      const overflowsTop = targetBounding.top < containerHeight + offset;
      if (
        overflowsTop ||
        (props.persistPlacement && state.initialPlacement === PLACEMENT.bottom)
      ) {
        position.top = verticalTargetOffset + targetBounding.height + offset;
        placement = PLACEMENT.bottom;
      } else {
        position.top = top;
      }
      break;
    }
    case 'bottom': {
      const targetBoundingMidPoint =
        horizontalTargetOffset + targetBounding.width / 2;

      let overflowsLeft = false;
      let overflowsRight = false;
      if (alignment === ALIGNMENT.middle) {
        overflowsRight =
          targetBoundingMidPoint + containerBounding.width / 2 >
          windowWidth + horizontalScrollOffset;
        overflowsLeft =
          horizontalTargetOffset - horizontalScrollOffset <
          containerBounding.width / 2;
      } else if (alignment === ALIGNMENT.end) {
        overflowsRight =
          targetBounding.right > windowWidth + horizontalScrollOffset;
        overflowsLeft =
          targetBounding.right - containerBounding.width <
          horizontalScrollOffset;
      } else if (alignment === ALIGNMENT.beginning) {
        overflowsLeft = targetBounding.left < horizontalScrollOffset;
        overflowsRight =
          targetBounding.left + containerBounding.width >
          windowWidth + horizontalScrollOffset;
      }

      if (overflowsRight && alignment === ALIGNMENT.beginning) {
        // if we are aligned to the beginning and we overflow to the right
        // then flip the alignment
        alignment = ALIGNMENT.end;
      } else if (overflowsLeft && alignment === ALIGNMENT.end) {
        // If we overflow to the left and the alignment is at the end
        // then flip the alignment
        alignment = ALIGNMENT.beginning;
      }

      if (alignment === ALIGNMENT.middle) {
        if (overflowsRight) {
          position.left = windowWidth + horizontalScrollOffset - containerWidth;
          // Hardcoded `CARET_SIZE` here is because the width of the caret is important!
          // we need to offset the position of the caret by the width of itself
          calculatedCaretPosition.left =
            caretPosition ||
            targetBoundingMidPoint - position.left - CARET_SIZE;
        } else if (overflowsLeft) {
          position.left = horizontalScrollOffset;
          calculatedCaretPosition.left =
            caretPosition ||
            targetBoundingMidPoint - position.left - CARET_SIZE;
        } else {
          position.left =
            horizontalTargetOffset -
            (containerBounding.width / 2 - targetBounding.width / 2);
          calculatedCaretPosition.left = caretPosition || CARET_POSITION;
        }
      } else if (alignment === ALIGNMENT.beginning) {
        // Bail on out of window calculations, these are "shotcuts" to just being positioned regularly
        calculatedCaretPosition.left = caretPosition || CARET_POSITION;
        position.left = horizontalTargetOffset;
      } else if (alignment === ALIGNMENT.end) {
        calculatedCaretPosition.right = caretPosition || CARET_POSITION;
        position.right =
          windowWidth - (targetBounding.width + targetBounding.left);
      }

      const top = targetBounding.top + targetBounding.height + offset;
      const overflowsBottom = top + containerHeight > win.innerHeight;
      // if passes overflow condition, or if overflow condition passed on initial open and tooltip should persist its direction
      if (
        overflowsBottom ||
        (props.persistPlacement && state.initialPlacement === 'top')
      ) {
        position.top =
          verticalTargetOffset - (containerBounding.height + offset);
        placement = 'top';
      } else {
        position.top = verticalTargetOffset + targetBounding.height + offset;
      }
      break;
    }
    case 'right': {
      const overflowsRight =
        targetBounding.right + containerWidth + offset > windowWidth;

      let overflowsTop = false;
      let overflowsBottom = false;
      if (alignment === ALIGNMENT.middle) {
        overflowsTop =
          targetBoundingMidPoint - containerBounding.height / 2 > 0;
        overflowsBottom =
          targetBoundingMidPoint + containerBounding.height / 2 >
          windowHeight + verticalScrollOffset;
      } else if (alignment === ALIGNMENT.beginning) {
        overflowsBottom =
          targetBounding.top + containerBounding.height >
          windowHeight + verticalScrollOffset;
        overflowsTop = targetBounding.top < 0;
      } else if (alignment === ALIGNMENT.end) {
        overflowsTop = targetBounding.bottom - containerBounding.height > 0;
        overflowsBottom =
          targetBounding.bottom > windowHeight + verticalScrollOffset;
      }

      if (overflowsTop && alignment === ALIGNMENT.end) {
        // if we are aligned to the end and we overflow to the top
        // then flip the alignment
        alignment = ALIGNMENT.beginning;
      } else if (overflowsBottom && alignment === ALIGNMENT.beginning) {
        // If we overflow to the bottom and the alignment is at the beginning
        // then flip the alignment
        alignment = ALIGNMENT.end;
      }

      // if passes overflow condition, or if overflow condition passed on initial open and tooltip should persist its direction
      if (
        overflowsRight ||
        (props.persistPlacement && state.initialPlacement === 'left')
      ) {
        position.left = horizontalTargetOffset - (offset + containerWidth);
        placement = 'left';
        calculatedCaretPosition.top = caretPosition || CARET_POSITION;
      } else {
        position.left = targetBounding.right + horizontalScrollOffset + offset;
        calculatedCaretPosition.top = caretPosition || CARET_POSITION;
      }
      if (props.alignment === ALIGNMENT.middle) {
        position.top =
          verticalTargetOffset +
          (targetBounding.height / 2 - containerBounding.height / 2);
        calculatedCaretPosition.top = caretPosition || CARET_POSITION;
      } else if (props.alignment === ALIGNMENT.beginning) {
        calculatedCaretPosition.top = caretPosition || CARET_POSITION;
        position.top = verticalTargetOffset;
      } else if (props.alignment === ALIGNMENT.end) {
        calculatedCaretPosition.bottom = caretPosition || CARET_POSITION;
        position.bottom = targetBounding.bottom;
      }
      break;
    }
    case 'left': {
      const overflowsLeft = targetBounding.left < containerWidth + offset;

      let overflowsTop = false;
      let overflowsBottom = false;
      if (alignment === ALIGNMENT.middle) {
        overflowsTop =
          targetBoundingMidPoint - containerBounding.height / 2 > 0;
        overflowsBottom =
          targetBoundingMidPoint + containerBounding.height / 2 >
          windowHeight + verticalScrollOffset;
      } else if (alignment === ALIGNMENT.beginning) {
        overflowsBottom =
          targetBounding.top + containerBounding.height >
          windowHeight + verticalScrollOffset;
        overflowsTop = targetBounding.top < 0;
      } else if (alignment === ALIGNMENT.end) {
        overflowsTop = targetBounding.bottom - containerBounding.height > 0;
        overflowsBottom =
          targetBounding.bottom > windowHeight + verticalScrollOffset;
      }

      if (overflowsTop && alignment === ALIGNMENT.end) {
        // if we are aligned to the end and we overflow to the top
        // then flip the alignment
        alignment = ALIGNMENT.beginning;
      } else if (overflowsBottom && alignment === ALIGNMENT.beginning) {
        // If we overflow to the bottom and the alignment is at the beginning
        // then flip the alignment
        alignment = ALIGNMENT.end;
      }

      // if passes overflow condition, or if overflow condition passed on initial open and tooltip should persist its direction
      if (
        overflowsLeft ||
        (props.persistPlacement && state.initialPlacement === 'right')
      ) {
        position.left = targetBounding.right + horizontalScrollOffset + offset;
        placement = 'right';
        calculatedCaretPosition.top = caretPosition || CARET_POSITION;
      } else {
        position.left = horizontalTargetOffset - (offset + containerWidth);
        calculatedCaretPosition.top = caretPosition || CARET_POSITION;
      }
      if (props.alignment === ALIGNMENT.middle) {
        position.top =
          verticalTargetOffset +
          (targetBounding.height / 2 - containerBounding.height / 2);
        calculatedCaretPosition.top = caretPosition || CARET_POSITION;
      } else if (props.alignment === ALIGNMENT.beginning) {
        position.top = verticalTargetOffset;
        calculatedCaretPosition.top = caretPosition || CARET_POSITION;
      } else if (props.alignment === ALIGNMENT.end) {
        position.bottom = targetBounding.bottom;
        calculatedCaretPosition.top = caretPosition || CARET_POSITION;
      }
      break;
    }
    default: {
      // default to top logic
      const targetBoundingMidPoint =
        horizontalTargetOffset + targetBounding.width / 2;

      let overflowsLeft = false;
      let overflowsRight = false;
      if (alignment === ALIGNMENT.middle) {
        overflowsRight =
          targetBoundingMidPoint + containerBounding.width / 2 >
          windowWidth + horizontalScrollOffset;
        overflowsLeft =
          horizontalTargetOffset - horizontalScrollOffset <
          containerBounding.width / 2;
      } else if (alignment === ALIGNMENT.end) {
        overflowsRight =
          targetBounding.right > windowWidth + horizontalScrollOffset;
        overflowsLeft =
          targetBounding.right - containerBounding.width <
          horizontalScrollOffset;
      } else if (alignment === ALIGNMENT.beginning) {
        overflowsLeft = targetBounding.left < horizontalScrollOffset;
        overflowsRight =
          targetBounding.left + containerBounding.width >
          windowWidth + horizontalScrollOffset;
      }

      if (overflowsRight && alignment === ALIGNMENT.beginning) {
        // if we are aligned to the beginning and we overflow to the right
        // then flip the alignment
        alignment = ALIGNMENT.end;
      } else if (overflowsLeft && alignment === ALIGNMENT.end) {
        // If we overflow to the left and the alignment is at the end
        // then flip the alignment
        alignment = ALIGNMENT.beginning;
      }

      if (props.alignment === ALIGNMENT.middle) {
        if (overflowsRight) {
          position.left = windowWidth + horizontalScrollOffset - containerWidth;
          // Hardcoded `CARET_SIZE` here is because the width of the caret is important!
          // we need to offset the position of the caret by the width of itself
          calculatedCaretPosition.left =
            caretPosition ||
            targetBoundingMidPoint - position.left - CARET_SIZE;
        } else if (overflowsLeft) {
          position.left = horizontalScrollOffset;
          calculatedCaretPosition.left =
            caretPosition ||
            targetBoundingMidPoint - position.left - CARET_SIZE;
        } else {
          position.left =
            horizontalTargetOffset -
            (containerBounding.width / 2 - targetBounding.width / 2);
          calculatedCaretPosition.left = caretPosition || CARET_POSITION;
        }
      } else if (props.alignment === ALIGNMENT.beginning) {
        calculatedCaretPosition.left = caretPosition || CARET_POSITION;
        position.left = horizontalTargetOffset;
      } else if (props.alignment === ALIGNMENT.end) {
        calculatedCaretPosition.right = caretPosition || CARET_POSITION;
        position.right = targetBounding.right;
      }
      const top = verticalTargetOffset - (targetBounding.height + offset);
      const overflowsTop = targetBounding.top < containerHeight + offset;
      // if passes overflow condition, or if overflow condition passed on initial open and tooltip should persist its direction
      if (
        overflowsTop ||
        (props.persistPlacement && state.initialPlacement === 'bottom')
      ) {
        position.top = verticalTargetOffset + targetBounding.height + offset;
        placement = 'bottom';
      } else {
        position.top = top;
      }
      break;
    }
  }

  return {
    position,
    alignment,
    placement:
      props.persistPlacement && state.initialPlacement !== null
        ? state.initialPlacement
        : placement,
    initialPlacement:
      state.initialPlacement === null ? placement : state.initialPlacement,
    caretPosition: calculatedCaretPosition,
    shouldUpdatePosition: false,
  };
};

export default class Positioner extends Component {
  static propTypes = {
    // A render function provided the needed refs plus styles
    children: PropTypes.func.isRequired,
    // A custom hook to allow for easier unit testing (i.e. where getBoundingClientRect doesn't exist)
    getBounding: PropTypes.func,
    // The initial placement of the positioned content relative to the target
    placement: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
    // If true the content will be open
    isOpen: PropTypes.bool,
    // Optional prop to configure the left or top alignment that the positioner returns
    // middle means that we find the middle of the target
    // beginning will be either left or top based off of placement (placement is vertical then its left, otherwise its top)
    // end will be either right or bottom based off of placement  (placement is vertical then its right, otherwise its bottom)
    alignment: PropTypes.oneOf(['middle', 'beginning', 'end']),
    // The element that the positioned content is positioned relative to
    targetRef: PropTypes.object,
    // The element that wraps the positioned content
    containerRef: PropTypes.object,
    // If true, the positioned element will persist its placement if its size changes
    persistPlacement: PropTypes.bool,
    // The distance (in px) the target element gets positioned (or offset) from the container element
    offset: PropTypes.number,

    // This is the percent offset from the left or the top of the positioned content
    // Note: it follows the alignment prop, if alignment is end and the position is top
    // this value is relative to the right
    // Any number between 0 and 1, or a string that represents a percentage
    caretPosition: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  };
  static defaultProps = {
    // This method can optionally return a maxWidth or maxHeight
    getBounding: (element) => element.getBoundingClientRect(),
    placement: PLACEMENT.top,
    isOpen: false,
    alignment: ALIGNMENT.middle,
    targetRef: null,
    containerRef: null,
    persistPlacement: false,
    offset: 0,
    caretPosition: null,
  };

  state = {
    // simply storing this in state so we have access to it in gDSFP
    isOpen: this.props.isOpen,
    position: {
      left: 0,
      top: 0,
    },
    placement: this.props.placement,
    initialPlacement: null,
    caretPosition: {
      left: CARET_POSITION,
    },
    container: this.props.containerRef,
    target: this.props.targetRef,
    alignment: this.props.alignment,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const newState = {isOpen: nextProps.isOpen};

    if (
      nextProps.containerRef &&
      nextProps.containerRef !== prevState.container
    ) {
      newState.container = nextProps.containerRef;
    }

    if (nextProps.targetRef && nextProps.targetRef !== prevState.target) {
      newState.target = nextProps.targetRef;
    }

    if (
      nextProps.isOpen &&
      !prevState.isOpen &&
      prevState.container &&
      prevState.target
    ) {
      newState.shouldUpdatePosition = true;
    } else {
      newState.shouldUpdatePosition = false;
    }
    return newState;
  }

  componentDidMount() {
    this.hasMounted = true;
    if (this.props.isOpen && this.target && this.container) {
      this.updatePosition();
    }
    window.addEventListener('resize', this.updatePosition);
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      !this.state.isOpen &&
      prevState.isOpen &&
      prevState.initialPlacement !== null
    ) {
      this.setState({
        initialPlacement: null,
      });
    }
    if (this.state.shouldUpdatePosition) {
      this.updatePosition();
    }
  }

  componentWillUnmount() {
    this.hasMounted = false;
    window.removeEventListener('resize', this.updatePosition);
  }

  static ALIGNMENT = ALIGNMENT;

  static PLACEMENT = PLACEMENT;

  hasMounted = false;

  updatePosition = () => {
    if (this.hasMounted) {
      this.setState(calculatePosition(document, window));
    }
  };

  assignContainerRef = (ref) => {
    if (this.hasMounted || ref !== null) {
      this.setState({container: ref});
    }
  };

  assignTargetRef = (ref) => {
    if (this.hasMounted || ref !== null) {
      this.setState({target: ref});
    }
  };

  render() {
    return this.props.children({
      assignTargetRef: this.assignTargetRef,
      assignContainerRef: this.assignContainerRef,
      updatePosition: this.updatePosition,
      // pass down position, placement, and caretPosition
      ...this.state,
    });
  }
}
